import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { AccountingStartRequest } from '@/api/handlers/resources/organizations/requests';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { AccountingStartResult } from '@/api/models';
import { OrganizationQueryKeys } from './organizationQueryKeys';

/**
 * Starts accounting (`POST /organizations/me/onboarding/accounting-start`,
 * OrgAdmin): generates the fiscal year and, when needed, its tax rates.
 *
 * Unwraps the envelope (rulebook §10): a refusal rejects with the
 * `ErrorResponse`, whose `code` is `FISCAL_YEAR_CLOSED` when every period of
 * the year is closed or locked, which the Accounting start step shows on its
 * year field. On success the organisation is invalidated, so it reads
 * `accountingStartNeeded: false`.
 */
export const useAccountingStartMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        AccountingStartResult,
        ErrorResponse,
        AccountingStartRequest
    >({
        mutationFn: async (body) => {
            const result = await apiClient.organizations.accountingStart(body);
            if (!result.success) {
                throw result.error;
            }
            return result.response;
        },
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: OrganizationQueryKeys.me(),
            }),
    });
};
